import { useParams, Link } from "react-router-dom";
import { ContactForm } from "@/components/ContactForm";

const CONTENT = {
  about: {
    title: "About Us",
    body: [
      "Mulan Peptide Collection LLC supplies high-purity research peptides for laboratory and research use only.",
      "Every compound in our catalog is third-party verified by HPLC and mass spectrometry for identity, net content and purity, and is handled under cold-chain conditions to preserve integrity.",
    ],
    sections: [
      {
        h: "Our Vision",
        p: "To be the most trusted name in research peptides — advancing scientific discovery through uncompromising purity, transparency, and integrity, and empowering researchers worldwide to push the boundaries of what's possible.",
      },
      {
        h: "Our Mission",
        p: "Our mission is to provide researchers with consistently high-purity, independently verified peptides backed by transparent Certificates of Analysis. We are committed to rigorous quality control, dependable cold-chain handling, fast and discreet fulfillment, and responsive support — so every experiment starts with materials you can trust.",
      },
    ],
  },
  contact: {
    title: "Contact",
    body: [
      "Questions about an order or a compound? We're here to help.",
      "Email: support@mulanpeptidecollection.com",
      "Location: USA",
    ],
  },
  disclaimer: {
    title: "Disclaimer",
    body: [
      "All products sold by Mulan Peptide Collection LLC are intended strictly for laboratory and research purposes only.",
      "Products are NOT drugs, foods, cosmetics, or dietary supplements, and are NOT intended for diagnosis, treatment, or prevention of any disease.",
      "By purchasing, you confirm you are a qualified researcher and will handle all products in accordance with applicable laws and regulations.",
    ],
  },
  privacy: {
    title: "Privacy Policy",
    body: [
      "We collect only the information necessary to process and fulfill your orders, including your name, email, shipping details and payment confirmation.",
      "Payment information for manual methods (Cash App, Zelle, Google Pay, Apple Pay) is sent directly by you to us through those apps; we never see or store your card or bank details.",
      "We never sell your personal information. We may use your email to send order updates and, if applicable, restock or cart reminders.",
    ],
  },
  terms: {
    title: "Terms & Conditions",
    body: [
      "By placing an order you agree that all products are purchased for research use only and that you are legally permitted to receive them.",
      "Prices, availability and product specifications are subject to change without notice.",
      "Title and risk of loss pass to the buyer upon delivery to the carrier.",
    ],
  },
  refund: {
    title: "Refund Policy",
    body: [
      "Due to the sensitive nature of research materials, opened or used products cannot be returned.",
      "If your order arrives damaged or incorrect, contact us within 7 days of delivery with your order reference and photos and we will make it right.",
      "Approved refunds are issued to the original payment method.",
    ],
  },
  shipping: {
    title: "Shipping Policy",
    body: [
      "We ship to the United States. Shipping is calculated by package weight and shown in your cart before checkout. Express is available for faster delivery, and qualifying orders ship free (standard).",
      "Orders placed before the daily cutoff ship the same day; tracking is provided on every order.",
      "For orders to Jamaica, please email support@mulanpeptidecollection.com before ordering.",
    ],
  },
};

export default function InfoPage() {
  const { slug } = useParams();
  const page = CONTENT[slug];

  if (!page) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-head text-3xl mb-4">Page not found</h1>
        <Link to="/" className="text-primary hover:underline">Return home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-20" data-testid={`info-${slug}`}>
      <span className="label-mono text-primary">Mulan Peptide Collection</span>
      <h1 className="font-head text-4xl mt-2 mb-8">{page.title}</h1>
      <div className="space-y-5">
        {page.body.map((p, i) => (
          <p key={i} className="text-muted-foreground leading-relaxed">{p}</p>
        ))}
      </div>
      {page.sections?.map((s, i) => (
        <div key={i} className="mt-12 border-l-2 border-primary/50 pl-6">
          <h2 className="font-head text-2xl text-foreground mb-3">{s.h}</h2>
          <p className="text-muted-foreground leading-relaxed">{s.p}</p>
        </div>
      ))}
      {slug === "contact" && <ContactForm />}
    </div>
  );
}
